import { useState } from 'react';
import { VStack, Text } from 'native-base';

// Firebase
import { httpsCallable } from 'firebase/functions';
import { functions } from '@breakroom-nx/apis';
import {
  getAuth,
  sendEmailVerification,
  RecaptchaVerifier,
  multiFactor,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
} from 'firebase/auth';

// Components
import { PrimaryButton, ModalWrapper } from '../../';

// Redux
import { useAppDispatch, openModal } from '@breakroom-nx/redux';

import * as Yup from 'yup';

import { Formik, ErrorMessage } from 'formik';

// Assets
import { theme } from '@breakroom-nx/assets';

import { Status } from '@breakroom-nx/helpers';
// import 'react-phone-number-input/style.css';
import { isValidPhoneNumber } from 'react-phone-number-input';
import PhoneInput from 'react-phone-number-input/react-native-input';

const phoneRegExp = /^\+[0-9\s']+$/;
const PhoneScheme = Yup.object().shape({
  phone: Yup.string()
    .matches(phoneRegExp, "Only letters, numbers and '_' underscores allowed")
    .required('Phone number required'),
});

const initialValues = {
  phone: '',
};

export const Enable2FA = () => {
  const dispatch = useAppDispatch();
  const [value, setValue] = useState();
  const [disabled, setDisabledState] = useState<boolean>(false); // Edit later
  const [focused, setFocused] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addPointsToUser = httpsCallable(functions, 'user-addPointsToUser');

  const auth = getAuth();
  const isEmailVerified = auth.currentUser?.emailVerified;

  const verifyEmail = () => {
    sendEmailVerification(auth.currentUser!)
      .then(() => {
        dispatch(
          openModal({
            modalType: 'Alert',
            modalProps: {
              status: Status.success,
              title: 'Verification Email Sent',
            },
          })
        );
      })
      .catch((error) => {
        dispatch(
          openModal({
            modalType: 'Alert',
            modalProps: {
              status: Status.error,
              title: 'Unknown error occurred, please try again.',
            },
          })
        );
      });
  };

  return (
    <ModalWrapper>
      <VStack
        bg={'surface.100'}
        space={3}
        textAlign={'center'}
        style={{
          borderColor: theme.colors.surface[200],
          borderWidth: 1,
          borderRadius: 25,
          shadowColor: theme.colors.surface[100],
          shadowOpacity: 0.25,
          shadowRadius: 20,
          shadowOffset: {
            width: 10,
            height: 20,
          },
          padding: 30,
        }}
      >
        <VStack space={5}>
          <VStack space={5}>
            <Text
              fontFamily={'heading'}
              fontSize={23}
              color={theme.colors.primary[400]}
              fontWeight={600}
              mb={'3px'}
            >
              Secure Your Account
            </Text>
            <Text
              fontFamily={'heading'}
              fontSize={16}
              color={'rgba(66, 211, 189, 0.60)'}
              fontWeight={600}
              mb={'3px'}
            >
              {isEmailVerified
                ? 'Enable 2FA and receive SMS codes whenever you log in or make important security changes to your account.'
                : 'In order to enable 2FA, we need your email to be verified. Please click the button below and check your inbox to complete verification.'}
            </Text>
          </VStack>
          {/* FORM */}

          {isEmailVerified ? (
            <Formik
              initialValues={initialValues}
              validationSchema={PhoneScheme}
              onSubmit={async (values, { setSubmitting }) => {
                setError(null);
                if (isValidPhoneNumber(values.phone)) {
                  const recaptchaVerifier = new RecaptchaVerifier(
                    'phone-auth-button',
                    {
                      size: 'invisible',
                      callback: function (response: any) {
                        // reCAPTCHA solved, you can proceed with
                        // phoneAuthProvider.verifyPhoneNumber(...).
                        // console.log(`Response: ${response}`);
                      },
                    },
                    auth
                  );

                  multiFactor(auth.currentUser!)
                    .getSession()
                    .then(function (multiFactorSession) {
                      // Specify the phone number and pass the MFA session.
                      const phoneInfoOptions = {
                        phoneNumber: values.phone,
                        session: multiFactorSession,
                      };

                      const phoneAuthProvider = new PhoneAuthProvider(auth);

                      // Send SMS verification code.
                      return phoneAuthProvider.verifyPhoneNumber(
                        phoneInfoOptions,
                        recaptchaVerifier
                      );
                    })
                    .then(function (verificationId) {
                      // Ask user for the verification code. Then:
                      const cred = PhoneAuthProvider.credential(
                        verificationId,
                        '123098'
                      );
                      const multiFactorAssertion =
                        PhoneMultiFactorGenerator.assertion(cred);

                      // Complete enrollment.
                      return multiFactor(auth.currentUser!)
                        .enroll(multiFactorAssertion, 'Phone Number')
                        .then(async (enrolled) => {
                          // Add Points to User
                          await addPointsToUser({ points: 500 }).then(() => {
                            dispatch(
                              openModal({
                                modalType: 'Alert',
                                modalProps: {
                                  status: Status.success,
                                  title: "You're all set!",
                                  description:
                                    'Thank you for making our platform more secure!',
                                },
                              })
                            );
                          });
                        });
                    });
                } else {
                  setSubmitting(false);
                  setError(
                    'Invalid Phone Number. Please check your number for any mistakes and try again'
                  );
                }
              }}
            >
              {({
                isSubmitting,
                handleBlur,
                handleSubmit,
                setFieldValue,
                values,
              }) => (
                <VStack space={'3'} width={'full'}>
                  <PhoneInput
                    placeholder="Phone Number"
                    name="phone"
                    value={values.phone}
                    onChange={(e) => setFieldValue('phone', e)}
                    onBlur={handleBlur('phone')}
                    autoFocus
                    isDisabled={isSubmitting}
                    style={{
                      boxShadow: 'none',
                      color: 'white',
                      background: 'transparent',
                      flex: 1,
                      paddingLeft: -1,
                      paddingBottom: 3,
                      placeholderColor: 'white',
                      height: 'full',
                      fontSize: 20,
                      fontWeight: 400,
                      letterSpacing: '-0.3px',
                      alignSelf: 'stretch',
                      borderBottomWidth: 2,
                      borderRadius: 0,
                      borderColor: theme.colors.primary[400],
                    }}
                  />
                  <Text variant={'error'}>
                    {error ? error : <ErrorMessage name="phone" />}
                  </Text>
                  <VStack alignItems={'center'}>
                    <PrimaryButton
                      disabled={values.phone === '' || isSubmitting}
                      text={'Enroll'}
                      bg={theme.colors.primary[400]}
                      color={'surface.100'}
                      isLoading={isSubmitting}
                      onPress={handleSubmit}
                      width={'100%'}
                    />
                    <div id="phone-auth-button" />
                  </VStack>
                </VStack>
              )}
            </Formik>
          ) : (
            <VStack>
              <Text
                textAlign={'center'}
                fontFamily={'heading'}
                fontSize={16}
                color={theme.colors.surface[200]}
                fontWeight={600}
                mb={'3px'}
              ></Text>
              <PrimaryButton
                disabled={disabled}
                text={'Verify Email'}
                bg={theme.colors.primary[400]}
                color={'surface.100'}
                onPress={verifyEmail}
                width={'100%'}
              />
            </VStack>
          )}
        </VStack>
      </VStack>
    </ModalWrapper>
  );
};
